
import { defineComponent, inject, onMounted, reactive, ref, toRefs } from 'vue';
import { AddSharp as CreateIcon, PencilOutline as EditIcon, MailOutline as SendIcon, TrashBinOutline as DeleteIcon, ChevronDownOutline as CaretIcon } from '@vicons/ionicons5';
import { useRouter } from 'vue-router';
import { PageState, Survey, RegionDTO } from '@/types';
import { useFormatters } from '@/composables/useFormatters';
import LoadingBar from '@/objects/loading-bar';
import surveyService from '@/services/survey-service';
import ExportCSVModal from '@/components/admin/ExportCSVModal.vue';
import SendModal from '@/components/admin/SendModal.vue';
import DuplicateSurveyModal from '@/components/admin/DuplicateSurveyModal.vue';
import ConfirmSurveyDeleteModal from '../survey/ConfirmSurveyDeleteModal.vue';
import CannotDeleteSurveyModal from '../survey/CannotDeleteSurveyModal.vue';
import { useAuth0 } from '../../auth/index';
import { ShowToast } from '@/providers/Toast/toast-types';

export default defineComponent({
  name: 'SurveyAdminList',
  components: { ExportCSVModal, SendModal, DuplicateSurveyModal, CreateIcon, EditIcon, SendIcon, DeleteIcon, CaretIcon, ConfirmSurveyDeleteModal, CannotDeleteSurveyModal },
  setup() {
    const { formatDate } = useFormatters();
    const state = reactive<
      PageState<Survey[]> & {
        showExportModal: boolean;
        exportingSurvey: Survey | null;
        showSendModal: boolean;
        surveyId: string;
        surveyName: string;
        selectedSurvey: Survey | null;
        showConfirmDeleteModal: boolean;
        showCannotDeleteModal: boolean;
        showDuplicateModal: boolean;
        duplicatingId: string | null;
      }
    >({
      loaded: false,
      data: [] as Survey[],
      showExportModal: false,
      exportingSurvey: null,
      showSendModal: false,
      surveyId: '',
      surveyName: '',
      selectedSurvey: null,
      showConfirmDeleteModal: false,
      showCannotDeleteModal: false,
      showDuplicateModal: false,
      duplicatingId: null
    });

    const rowActionOptions = [
      { label: 'Send', key: 'send' },
      { label: 'Export', key: 'export' },
      { type: 'divider', key: 'actions-divider' },
      { label: 'Duplicate', key: 'duplicate' }
    ];
    const { isAdmin } = useAuth0().state;

    const showToast = inject<ShowToast>('showToast')!;
    const loadingBar = new LoadingBar();
    const surveyRegions = ref(new Set<RegionDTO>());
    const router = useRouter();
    loadingBar.start();

    onMounted(async () => {
      loadingBar.start();
      try {
        const list = await surveyService.getList();
        state.data = list;
        state.loaded = true;

        const uniqueRegionIds = list.reduce((acc, survey) => {
          if (!survey.region) return acc;
          if (survey.region.id && !acc.has(survey.region.id)) {
            acc.add(survey.region.id);
          }
          return acc;
        }, new Set<string>());
        // map to region names
        surveyRegions.value = new Set<RegionDTO>(
          Array.from(uniqueRegionIds).map((id) => {
            const survey = list.find((s) => s.region?.id === id);
            return survey?.region as RegionDTO;
          })
        );
        loadingBar.finish();
      } catch (error) {
        loadingBar.error();
        showToast({
          headline: 'Error',
          message: 'Failed to load surveys or user data',
          color: 'danger'
        });
      }
    });

    const removeSurveyFromList = (id: string) => {
      state.data = state.data.filter((survey) => survey.id !== id);
    };

    const addSurveyToList = (survey: Survey) => {
      // Sorted by createdOn descending, so the copy lands on top without a reload.
      state.data = [survey, ...state.data];
    };

    // `duplicate-finished` fires from the modal's `finally`, so the row clears either way.
    const onDuplicateStarted = (surveyId: string) => {
      state.duplicatingId = surveyId;
    };

    const onDuplicateFinished = (surveyId: string) => {
      if (state.duplicatingId === surveyId) state.duplicatingId = null;
    };

    const deleteSurvey = async (id: string) => {
      state.selectedSurvey = state.data.find((survey) => survey.id === id) ?? null;
      if (!state.selectedSurvey) {
        showToast({
          headline: 'Error',
          message: 'Failed to find survey',
          color: 'danger'
        });
        return;
      }

      if ((state.selectedSurvey?.completedCount ?? 0) > 0) {
        state.showCannotDeleteModal = true;
        return;
      }
      state.showConfirmDeleteModal = true;
    };

    const sendSurvey = (id: string, surveyName: string): void => {
      loadingBar.finish();
      state.surveyId = id;
      state.surveyName = surveyName;
      state.showSendModal = true;
    };

    const addToSentCount = (sent: number): void => {
      const survey = state.data.find((s) => s.id === state.surveyId);
      if (survey) survey.sentCount += sent;
    };

    const exportSurvey = (survey: Survey): void => {
      state.exportingSurvey = survey;
      state.showExportModal = true;
    };

    const duplicateSurvey = (survey: Survey): void => {
      state.selectedSurvey = survey;
      state.showDuplicateModal = true;
    };

    // Calls the row's own handlers rather than repeating them, so the paths cannot drift.
    const onRowAction = (key: string, survey: Survey): void => {
      if (key === 'send') {
        sendSurvey(survey.id, survey.name);
      } else if (key === 'export') {
        exportSurvey(survey);
      } else if (key === 'duplicate') {
        duplicateSurvey(survey);
      }
    };

    return {
      ...toRefs(state),
      createSurvey: (): void => {
        router.push(`/admin/survey/new`);
      },
      viewResults: (id: string): void => {
        router.push(`/admin/survey/${id}/results`);
      },
      editSurvey: (id: string): void => {
        router.push(`/admin/survey/${id}`);
      },
      sendSurvey,
      addToSentCount,
      exportSurvey,
      duplicateSurvey,
      onRowAction,
      onDuplicateStarted,
      onDuplicateFinished,
      rowActionOptions,
      formatDate,
      surveyRegions,
      isAdmin,
      deleteSurvey,
      removeSurveyFromList,
      addSurveyToList
    };
  }
});
